import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Select from 'react-select';
import { 
  catalogApi, 
  registerConfigApi, 
  createRegisterApi,
  templateApi,
  type RegisterConfigKind 
} from '../services/api';
import { useToast } from '../hooks/useToast';
import { useSwal } from '../hooks/useSwal';
import { NimbusTable } from '../components/NimbusTable';
import { NimbusModal, NimbusModalContent } from '../components/NimbusModal';
import { PageHeader } from '../components/PageHeader';

type CatalogField = {
  fieldId: string;
  fieldName?: string;
  fieldType?: string;
};

type FieldConfigRecord = {
  // The config lambda (lambda/prod-nimbus-config, shared by Evidence/Risk/Incident/Issue/
  // Remediation/Threat) always returns this row's id as configId - confirmed live. This type
  // used to say configKey, which no response ever actually has, so every edit/delete here
  // read undefined and PUT/DELETE went to /config/undefined, 404ing every time.
  configId: string;
  fieldId: string;
  fieldName: string;
  fieldType: string;
  cardNumber: number;
  displayOrder: number;
  isRequired: boolean;
  isVisible: boolean;
  isEditable: boolean;
  defaultValue?: any;
  placeholder?: string;
  helpText?: string;
};

type TemplateOption = { value: string; label: string };
type TemplateAlert = {
  variant: 'warning' | 'info';
  iconClass: string;
  message: string;
};

type TemplateConfig = {
  intro: string;
  options: TemplateOption[];
  details?: string;
  alert: TemplateAlert;
};

const sortConfigs = (configs: FieldConfigRecord[]) => {
  return [...configs].sort((a, b) => {
    if (a.cardNumber !== b.cardNumber) return a.cardNumber - b.cardNumber;
    return (a.displayOrder || 0) - (b.displayOrder || 0);
  });
};

const normalizeCards = (cards: Record<string, string>) => {
  const next: Record<string, string> = {};
  Object.keys(cards || {}).forEach((k) => {
    const n = parseInt(k, 10);
    if (!Number.isFinite(n) || n <= 0 || k === 'undefined') return;
    const v = String((cards as any)[k] ?? '').trim();
    if (!v) return;
    next[String(n)] = v;
  });
  return next;
};

const defaultCardName = (cardNumber: number): string => {
  const defaults: Record<number, string> = {
    1: 'Basic Information',
    2: 'Risk Assessment',
    3: 'Risk Treatment',
    4: 'Additional Details',
    5: 'Monitoring & Review',
    6: 'Impact Analysis',
    7: 'Review & Monitoring',
    8: 'Compliance',
    9: 'Documentation',
  };
  return defaults[cardNumber] || `Card ${cardNumber}`;
};

const kindDisplayName = (kind: RegisterConfigKind): string => {
  if (kind === 'risk') return 'Risk';
  if (kind === 'incident') return 'Incident';
  if (kind === 'issue') return 'Issue';
  if (kind === 'remediation') return 'Remediation';
  if (kind === 'threat') return 'Threat';
  return 'Evidence';
};

const kindListPath = (kind: RegisterConfigKind): string => {
  if (kind === 'risk') return '/risks';
  if (kind === 'incident') return '/incidents';
  if (kind === 'issue') return '/issues';
  if (kind === 'remediation') return '/remediations';
  if (kind === 'threat') return '/threats';
  return '/evidence';
};

type Props = {
  kind: RegisterConfigKind;
};

export const RegisterConfigPage: React.FC<Props> = ({ kind }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const swal = useSwal();

  const registerId = searchParams.get('registerId') || '';
  const kindName = kindDisplayName(kind);
  const listPath = kindListPath(kind);

  // Fetch templates dynamically from the API
  const { data: templatesData = [] } = useQuery({
    queryKey: ['templates', kind],
    queryFn: () => templateApi.list(kind),
  });

  // Create template configuration from API data
  const templateConfig = useMemo((): TemplateConfig => {
    const activeTemplates = templatesData.filter((t) => t.isActive);
    
    if (activeTemplates.length === 0) {
      // Fallback if no templates are available
      return {
        intro: `No templates available for ${kindName} registers.`,
        options: [],
        alert: {
          variant: 'info',
          iconClass: 'ri-information-line',
          message: 'No templates have been configured for this category yet.',
        },
      };
    }

    // Build template options from active templates
    const options = activeTemplates.map((t) => ({
      value: t.templateName,
      label: t.displayName,
    }));

    return {
      intro: `Apply a template to add pre-configured fields to this ${kindName.toLowerCase()} register.`,
      options,
      details: activeTemplates.length > 1 
        ? 'Select a template below to preview its field configuration.' 
        : undefined,
      alert: {
        variant: 'warning',
        iconClass: 'ri-alert-line',
        message: 'This will add template fields to your existing configuration.',
      },
    };
  }, [templatesData, kindName]);

  // Auto-generate API based on register type
  const registerApi = useMemo(() => createRegisterApi(kind), [kind]);

  const { data: register } = useQuery<any>({
    queryKey: ['register', registerId],
    queryFn: () => registerApi.getOne(registerId),
    enabled: !!registerId,
  });

  const { data: catalogFields = [] } = useQuery<CatalogField[]>({
    queryKey: ['catalog', kind],
    queryFn: async () => catalogApi.listByCategory(kind),
  });

  const {
    data: configs = [],
    isLoading: isLoadingConfigs,
    error: configsError,
  } = useQuery<FieldConfigRecord[]>({
    queryKey: ['registerFieldConfigs', kind, registerId],
    queryFn: async () => registerConfigApi.getFieldConfigs(kind, registerId),
    enabled: !!registerId,
  });

  const {
    data: cardsRaw = {},
    isLoading: isLoadingCards,
  } = useQuery<Record<string, string>>({
    queryKey: ['registerCardDefinitions', kind, registerId],
    queryFn: async () => registerConfigApi.getCardDefinitions(kind, registerId),
    enabled: !!registerId,
  });

  const cards = useMemo(() => {
    const normalized = normalizeCards(cardsRaw || {});
    if (Object.keys(normalized).length > 0) return normalized;
    const next: Record<string, string> = {};
    for (let i = 1; i <= 5; i += 1) next[String(i)] = defaultCardName(i);
    (configs || []).forEach((c) => {
      const n = Number(c.cardNumber || 0);
      if (!n || n <= 0) return;
      if (!next[String(n)]) next[String(n)] = defaultCardName(n);
    });
    return next;
  }, [cardsRaw, configs]);

  const registerName = (register as any)?.name || registerId || 'Register';

  const [showAddField, setShowAddField] = useState(false);
  const [showEditField, setShowEditField] = useState(false);
  const [showTemplate, setShowTemplate] = useState(false);
  const [showManageCards, setShowManageCards] = useState(false);
  const [showEditCard, setShowEditCard] = useState(false);

  // Add field state
  const [addFieldId, setAddFieldId] = useState<string[]>([]);
  const [addCardNumber, setAddCardNumber] = useState<number>(1);
  const [addDisplayOrder, setAddDisplayOrder] = useState<number>(1);
  const [addRequired, setAddRequired] = useState(false);
  const [addVisible, setAddVisible] = useState(true);
  const [addEditable, setAddEditable] = useState(true);
  const [addDefaultValue, setAddDefaultValue] = useState('');
  const [addPlaceholder, setAddPlaceholder] = useState('');
  const [addHelpText, setAddHelpText] = useState('');

  // Edit field state
  const [editConfigId, setEditConfigId] = useState('');
  const [editFieldName, setEditFieldName] = useState('');
  const [editFieldType, setEditFieldType] = useState('text');
  const [editCardNumber, setEditCardNumber] = useState<number>(1);
  const [editDisplayOrder, setEditDisplayOrder] = useState<number>(1);
  const [editRequired, setEditRequired] = useState(false);
  const [editVisible, setEditVisible] = useState(true);
  const [editEditable, setEditEditable] = useState(true);
  const [editDefaultValue, setEditDefaultValue] = useState('');
  const [editPlaceholder, setEditPlaceholder] = useState('');
  const [editHelpText, setEditHelpText] = useState('');

  // Cards state
  const [addCardMode, setAddCardMode] = useState(false);
  const [newCardNumber, setNewCardNumber] = useState<number>(1);
  const [newCardName, setNewCardName] = useState<string>('');
  const [editCardNumberValue, setEditCardNumberValue] = useState<number>(1);
  const [editCardNameValue, setEditCardNameValue] = useState<string>('');

  // Template state - will be set when templates are loaded
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');

  // Update selected template when templates are loaded
  React.useEffect(() => {
    if (templateConfig.options.length > 0 && !selectedTemplate) {
      setSelectedTemplate(templateConfig.options[0].value);
    }
  }, [templateConfig.options, selectedTemplate]);

  const availableCatalogFields = useMemo(() => {
    const existingIds = new Set((configs || []).map((c) => c.fieldId));
    return (catalogFields || [])
      .filter((f) => !!f.fieldId && !existingIds.has(f.fieldId))
      .sort((a, b) => (a.fieldName || a.fieldId).localeCompare(b.fieldName || b.fieldId));
  }, [catalogFields, configs]);

  const cardOptions = useMemo(() => {
    return Object.keys(cards)
      .map((k) => parseInt(k, 10))
      .filter((n) => Number.isFinite(n) && n > 0)
      .sort((a, b) => a - b)
      .map((n) => ({ value: n, label: `${n} - ${cards[String(n)]}` }));
  }, [cards]);

  const invalidateAll = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['registerFieldConfigs', kind, registerId] }),
      qc.invalidateQueries({ queryKey: ['registerCardDefinitions', kind, registerId] }),
    ]);
  };

  const addFieldMutation = useMutation({
    mutationFn: async () => {
      if (!addFieldId || addFieldId.length === 0) throw new Error('No field selected');
      
      // Create all selected fields sequentially
      for (const fieldId of addFieldId) {
        const payload = {
          fieldId: fieldId,
          cardNumber: addCardNumber,
          displayOrder: addDisplayOrder,
          isRequired: addRequired,
          isVisible: addVisible,
          isEditable: addEditable,
          defaultValue: addDefaultValue,
          placeholder: addPlaceholder,
          helpText: addHelpText,
        };
        await registerConfigApi.createFieldConfig(kind, registerId, payload);
      }
    },
    onSuccess: async () => {
      setShowAddField(false);
      setAddFieldId([]);
      setAddDisplayOrder(1);
      setAddRequired(false);
      setAddVisible(true);
      setAddEditable(true);
      setAddDefaultValue('');
      setAddPlaceholder('');
      setAddHelpText('');
      await invalidateAll();
    },
    onError: () => {
      toast.error('Failed to add field');
    },
  });

  const updateFieldMutation = useMutation({
    mutationFn: async () => {
      const payload: any = {
        fieldType: editFieldType,
        cardNumber: editCardNumber,
        displayOrder: editDisplayOrder,
        isRequired: editRequired,
        isVisible: editVisible,
        isEditable: editEditable,
        defaultValue: editDefaultValue.trim() || null,
        placeholder: editPlaceholder.trim() || null,
        helpText: editHelpText.trim() || null,
      };
      await registerConfigApi.updateFieldConfig(editConfigId, payload);
    },
    onSuccess: async () => {
      setShowEditField(false);
      await invalidateAll();
      toast.success('Field updated.');
    },
    onError: (err: any) => {
      console.error(err);
      toast.error('Failed to update field');
    },
  });

  const deleteFieldMutation = useMutation({
    mutationFn: async (configId: string) => {
      await registerConfigApi.deleteFieldConfig(configId);
    },
    onSuccess: async () => {
      await invalidateAll();
      toast.success('Field removed.');
    },
    onError: () => {
      toast.error('Failed to remove field');
    },
  });

  const handleDeleteField = async (configId: string) => {
    const ok = await swal.confirm('Remove this field from the register?');
    if (!ok) return;
    deleteFieldMutation.mutate(configId);
  };

  const applyTemplateMutation = useMutation({
    mutationFn: async () => {
      await registerConfigApi.applyTemplate(kind, registerId, selectedTemplate);
    },
    onSuccess: async () => {
      setShowTemplate(false);
      await invalidateAll();
      toast.success('Template applied.');
    },
    onError: () => {
      toast.error('Failed to apply template');
    },
  });

  const updateCardsMutation = useMutation({
    mutationFn: async (nextCards: Record<string, string>) => {
      await registerConfigApi.updateCards(kind, registerId, nextCards);
    },
    onSuccess: async () => {
      await invalidateAll();
    },
    onError: () => {
      toast.error('Failed to save card definitions');
    },
  });

  const openEditFieldModal = (cfg: FieldConfigRecord) => {
    setEditConfigId(cfg.configId);
    setEditFieldName(cfg.fieldName);
    setEditFieldType(cfg.fieldType || 'text');
    setEditCardNumber(Number(cfg.cardNumber || 1));
    setEditDisplayOrder(Number(cfg.displayOrder || 1));
    setEditRequired(Boolean(cfg.isRequired));
    setEditVisible(Boolean(cfg.isVisible));
    setEditEditable(Boolean(cfg.isEditable));
    setEditDefaultValue(String(cfg.defaultValue ?? ''));
    setEditPlaceholder(String(cfg.placeholder ?? ''));
    setEditHelpText(String(cfg.helpText ?? ''));
    setShowEditField(true);
  };

  const openManageCardsModal = () => {
    setShowManageCards(true);
    setAddCardMode(false);
    const existingNumbers = Object.keys(cards)
      .map((k) => parseInt(k, 10))
      .filter((n) => Number.isFinite(n) && n > 0);
    const nextNum = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
    setNewCardNumber(nextNum);
    setNewCardName('');
  };

  const addCard = async () => {
    if (!newCardNumber || !newCardName.trim()) {
      toast.warning('Please provide both card number and name');
      return;
    }
    if (cards[String(newCardNumber)]) {
      toast.error('A card with this number already exists. Please use a different number or edit the existing card.');
      return;
    }
    const next = { ...cards, [String(newCardNumber)]: newCardName.trim() };
    await updateCardsMutation.mutateAsync(next);
    setAddCardMode(false);
  };

  const openEditCardModal = (cardNumber: number) => {
    setEditCardNumberValue(cardNumber);
    setEditCardNameValue(cards[String(cardNumber)] || '');
    setShowEditCard(true);
  };

  const saveEditedCard = async () => {
    if (!editCardNameValue.trim()) {
      toast.warning('Please provide a card name');
      return;
    }
    const next = { ...cards, [String(editCardNumberValue)]: editCardNameValue.trim() };
    await updateCardsMutation.mutateAsync(next);
    setShowEditCard(false);
  };

  const deleteCard = async (cardNumber: number) => {
    const using = (configs || []).filter((c) => Number(c.cardNumber) === cardNumber);
    if (using.length > 0) {
      toast.error(
        `Cannot delete this card. ${using.length} field(s) are currently assigned to it. Please reassign these fields first.`
      );
      return;
    }
    const ok = await swal.confirmDelete(`card ${cardNumber} - ${cards[String(cardNumber)]}`);
    if (!ok) return;
    const next = { ...cards };
    delete next[String(cardNumber)];
    await updateCardsMutation.mutateAsync(next);
  };

  if (!registerId) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="np-banner np-tone-warning">
          <i className="ri-error-warning-line"></i>
          <div>
            No register selected. Return to <a href="/registers">Registers</a>.
          </div>
        </div>
      </div>
    );
  }

  const loading = isLoadingConfigs || isLoadingCards;

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-settings-3-line"
        title={`Configure ${kindName} Fields`}
        breadcrumbs={[
          { label: 'Home', to: '/dashboard' },
          { label: 'Registers', to: '/registers' },
          { label: registerName, to: `${listPath}?registerId=${encodeURIComponent(registerId)}` },
          { label: 'Configure Fields' },
        ]}
        actions={
          <>
            <button
              className="np-btn np-btn-ghost"
              onClick={() => navigate(`${listPath}?registerId=${encodeURIComponent(registerId)}`)}
            >
              <i className="ri-arrow-left-line"></i> Back
            </button>
            <button className="np-btn np-btn-info" onClick={openManageCardsModal}>
              <i className="ri-layout-grid-line"></i> Manage Cards
            </button>
            <button className="np-btn np-btn-success" onClick={() => setShowAddField(true)}>
              <i className="ri-add-line"></i> Add Field
            </button>
            <button className="np-btn np-btn-primary" onClick={() => setShowTemplate(true)}>
              <i className="ri-file-copy-line"></i> Apply Template
            </button>
          </>
        }
      />

      {configsError ? (
        <div className="np-banner np-tone-danger mb-4">
          <i className="ri-error-warning-line"></i>
          Failed to load field configs.
        </div>
      ) : null}

      <div className="np-card">
        <div className="np-card-body">
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : configs.length === 0 ? (
            <div className="text-center py-5">
              <i className="ri-inbox-line ri-48px d-block mb-2" style={{ color: 'var(--bs-secondary-color)' }}></i>
              <p className="np-sub mb-0">No fields configured. Add fields or apply a template to get started.</p>
            </div>
          ) : (
            <NimbusTable thead={<tr>
                    <th>Field Name</th>
                    <th>Card</th>
                    <th>Order</th>
                    <th>Type</th>
                    <th>Required</th>
                    <th>Visible</th>
                    <th>Editable</th>
                    <th>Actions</th>
                  </tr>}>
                  {sortConfigs(configs).map((cfg) => (
                    <tr key={cfg.configId}>
                      <td>
                        <strong>{cfg.fieldName}</strong>
                      </td>
                      <td>
                        <span className="np-chip np-tone-primary np-tabular">{cfg.cardNumber}</span>
                      </td>
                      <td className="np-tabular">{cfg.displayOrder}</td>
                      <td>
                        <code>{cfg.fieldType}</code>
                      </td>
                      <td>
                        {cfg.isRequired ? (
                          <i className="ri-check-line text-success"></i>
                        ) : (
                          <i className="ri-close-line" style={{ color: 'var(--bs-secondary-color)' }}></i>
                        )}
                      </td>
                      <td>
                        {cfg.isVisible ? (
                          <i className="ri-check-line text-success"></i>
                        ) : (
                          <i className="ri-close-line" style={{ color: 'var(--bs-secondary-color)' }}></i>
                        )}
                      </td>
                      <td>
                        {cfg.isEditable ? (
                          <i className="ri-check-line text-success"></i>
                        ) : (
                          <i className="ri-close-line" style={{ color: 'var(--bs-secondary-color)' }}></i>
                        )}
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <button
                            className="np-icon-btn"
                            onClick={() => openEditFieldModal(cfg)}
                            title="Edit"
                          >
                            <i className="ri-edit-line"></i>
                          </button>
                          <button
                            className="np-icon-btn np-icon-btn-danger"
                            onClick={() => handleDeleteField(cfg.configId)}
                            title="Remove"
                            disabled={deleteFieldMutation.isPending}
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
            </NimbusTable>
          )}
        </div>
      </div>

      {/* Add Field */}
      {showAddField && (
        <NimbusModal size="lg">
          <NimbusModalContent
            title="Add Field"
            onClose={() => setShowAddField(false)}
            footer={
              <>
                <button className="np-btn np-btn-ghost" onClick={() => setShowAddField(false)}>
                  Cancel
                </button>
                <button className="np-btn np-btn-primary" onClick={() => addFieldMutation.mutate()} disabled={addFieldMutation.isPending}>
                  Add Field
                </button>
              </>
            }
          >
                <div className="np-field">
                  <label>Select Field from Catalog</label>
                  <Select
                    isMulti
                    value={availableCatalogFields
                      .filter((f) => addFieldId.includes(f.fieldId))
                      .map((f) => ({
                        value: f.fieldId,
                        label: `${f.fieldName || f.fieldId} (${f.fieldType || 'text'})`,
                      }))}
                    onChange={(selected) => {
                      setAddFieldId((selected || []).map((s) => s.value));
                    }}
                    options={availableCatalogFields.map((f) => ({
                      value: f.fieldId,
                      label: `${f.fieldName || f.fieldId} (${f.fieldType || 'text'})`,
                    }))}
                    placeholder="Select one or more fields…"
                    className="basic-multi-select"
                    classNamePrefix="select"
                  />
                </div>

                <div className="row">
                  <div className="col-md-6 np-field">
                    <label>Card Number</label>
                    <select
                      className="np-select"
                      value={String(addCardNumber)}
                      onChange={(e) => setAddCardNumber(parseInt(e.target.value, 10) || 1)}
                    >
                      {cardOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6 np-field">
                    <label>Display Order</label>
                    <input
                      type="number"
                      className="np-input np-tabular"
                      min={1}
                      value={addDisplayOrder}
                      onChange={(e) => setAddDisplayOrder(parseInt(e.target.value, 10) || 1)}
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-4 mb-3">
                    <div className="d-flex align-items-center gap-2">
                      <label className="np-switch mb-0">
                        <input type="checkbox" checked={addRequired} onChange={(e) => setAddRequired(e.target.checked)} id="addRequired" />
                        <span className="np-switch-track"></span>
                        <span className="np-switch-thumb"></span>
                      </label>
                      <label htmlFor="addRequired" className="mb-0">Required</label>
                    </div>
                  </div>
                  <div className="col-md-4 mb-3">
                    <div className="d-flex align-items-center gap-2">
                      <label className="np-switch mb-0">
                        <input type="checkbox" checked={addVisible} onChange={(e) => setAddVisible(e.target.checked)} id="addVisible" />
                        <span className="np-switch-track"></span>
                        <span className="np-switch-thumb"></span>
                      </label>
                      <label htmlFor="addVisible" className="mb-0">Visible</label>
                    </div>
                  </div>
                  <div className="col-md-4 mb-3">
                    <div className="d-flex align-items-center gap-2">
                      <label className="np-switch mb-0">
                        <input type="checkbox" checked={addEditable} onChange={(e) => setAddEditable(e.target.checked)} id="addEditable" />
                        <span className="np-switch-track"></span>
                        <span className="np-switch-thumb"></span>
                      </label>
                      <label htmlFor="addEditable" className="mb-0">Editable</label>
                    </div>
                  </div>
                </div>

                <div className="np-field">
                  <label>Default Value</label>
                  <input className="np-input" value={addDefaultValue} onChange={(e) => setAddDefaultValue(e.target.value)} />
                </div>
                <div className="np-field">
                  <label>Placeholder</label>
                  <input className="np-input" value={addPlaceholder} onChange={(e) => setAddPlaceholder(e.target.value)} />
                </div>
                <div className="np-field">
                  <label>Help Text</label>
                  <textarea className="np-textarea" rows={2} value={addHelpText} onChange={(e) => setAddHelpText(e.target.value)} />
                </div>
          </NimbusModalContent>
        </NimbusModal>
      )}

      {/* Edit Field */}
      {showEditField && (
        <NimbusModal size="lg">
          <NimbusModalContent
            title="Edit Field Configuration"
            onClose={() => setShowEditField(false)}
            footer={
              <>
                <button className="np-btn np-btn-ghost" onClick={() => setShowEditField(false)}>
                  Cancel
                </button>
                <button className="np-btn np-btn-primary" onClick={() => updateFieldMutation.mutate()} disabled={updateFieldMutation.isPending}>
                  Save Changes
                </button>
              </>
            }
          >
                <div className="np-field">
                  <label>Field Name</label>
                  <input className="np-input" value={editFieldName} readOnly />
                </div>

                <div className="np-field">
                  <label>Field Type</label>
                  <select className="np-select" value={editFieldType} onChange={(e) => setEditFieldType(e.target.value)}>
                    <option value="text">Text</option>
                    <option value="textarea">Textarea</option>
                    <option value="richtext">Rich Text</option>
                    <option value="number">Number</option>
                    <option value="email">Email</option>
                    <option value="url">URL</option>
                    <option value="date">Date</option>
                    <option value="select">Select</option>
                  </select>
                </div>

                <div className="row">
                  <div className="col-md-6 np-field">
                    <label>Card Number</label>
                    <select className="np-select" value={String(editCardNumber)} onChange={(e) => setEditCardNumber(parseInt(e.target.value, 10) || 1)}>
                      {cardOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6 np-field">
                    <label>Display Order</label>
                    <input type="number" className="np-input np-tabular" min={1} value={editDisplayOrder} onChange={(e) => setEditDisplayOrder(parseInt(e.target.value, 10) || 1)} />
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-4 mb-3">
                    <div className="d-flex align-items-center gap-2">
                      <label className="np-switch mb-0">
                        <input type="checkbox" checked={editRequired} onChange={(e) => setEditRequired(e.target.checked)} id="editRequired" />
                        <span className="np-switch-track"></span>
                        <span className="np-switch-thumb"></span>
                      </label>
                      <label htmlFor="editRequired" className="mb-0">Required</label>
                    </div>
                  </div>
                  <div className="col-md-4 mb-3">
                    <div className="d-flex align-items-center gap-2">
                      <label className="np-switch mb-0">
                        <input type="checkbox" checked={editVisible} onChange={(e) => setEditVisible(e.target.checked)} id="editVisible" />
                        <span className="np-switch-track"></span>
                        <span className="np-switch-thumb"></span>
                      </label>
                      <label htmlFor="editVisible" className="mb-0">Visible</label>
                    </div>
                  </div>
                  <div className="col-md-4 mb-3">
                    <div className="d-flex align-items-center gap-2">
                      <label className="np-switch mb-0">
                        <input type="checkbox" checked={editEditable} onChange={(e) => setEditEditable(e.target.checked)} id="editEditable" />
                        <span className="np-switch-track"></span>
                        <span className="np-switch-thumb"></span>
                      </label>
                      <label htmlFor="editEditable" className="mb-0">Editable</label>
                    </div>
                  </div>
                </div>

                <div className="np-field">
                  <label>Default Value</label>
                  <input className="np-input" value={editDefaultValue} onChange={(e) => setEditDefaultValue(e.target.value)} />
                </div>
                <div className="np-field">
                  <label>Placeholder</label>
                  <input className="np-input" value={editPlaceholder} onChange={(e) => setEditPlaceholder(e.target.value)} />
                </div>
                <div className="np-field">
                  <label>Help Text</label>
                  <textarea className="np-textarea" rows={2} value={editHelpText} onChange={(e) => setEditHelpText(e.target.value)} />
                </div>
          </NimbusModalContent>
        </NimbusModal>
      )}

      {/* Template */}
      {showTemplate && (
        <NimbusModal>
          <NimbusModalContent
            title="Apply Template"
            onClose={() => setShowTemplate(false)}
            footer={
              <>
                <button className="np-btn np-btn-ghost" onClick={() => setShowTemplate(false)}>
                  Cancel
                </button>
                <button className="np-btn np-btn-primary" onClick={() => applyTemplateMutation.mutate()} disabled={applyTemplateMutation.isPending}>
                  Apply Template
                </button>
              </>
            }
          >
                <p>{templateConfig.intro}</p>
                <div className="np-field">
                  <label>Template</label>
                  <select
                    className="np-select"
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                    disabled={templateConfig.options.length <= 1}
                  >
                    {templateConfig.options.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  {templateConfig.details ? (
                    <small className="np-hint d-block mt-2">{templateConfig.details}</small>
                  ) : null}
                </div>
                <div className={`np-banner np-tone-${templateConfig.alert.variant} mb-0`}>
                  <i className={templateConfig.alert.iconClass}></i>
                  <div>{templateConfig.alert.message}</div>
                </div>
          </NimbusModalContent>
        </NimbusModal>
      )}

      {/* Manage Cards */}
      {showManageCards && (
        <NimbusModal size="lg">
          <NimbusModalContent
            title="Manage Cards"
            onClose={() => setShowManageCards(false)}
            footer={
              <button className="np-btn np-btn-ghost" onClick={() => setShowManageCards(false)}>
                Close
              </button>
            }
          >
                <div className="mb-3">
                  <button className="np-btn np-btn-sm np-btn-primary" onClick={() => setAddCardMode(true)}>
                    <i className="ri-add-line"></i> Add New Card
                  </button>
                </div>

                {addCardMode && (
                  <div className="np-card mb-3">
                    <div className="np-card-head">
                      <h2>Add New Card</h2>
                    </div>
                    <div className="np-card-body">
                      <div className="row">
                        <div className="col-md-4 np-field">
                          <label>Card Number</label>
                          <input type="number" className="np-input np-tabular" min={1} max={20} value={newCardNumber} onChange={(e) => setNewCardNumber(parseInt(e.target.value, 10) || 1)} />
                        </div>
                        <div className="col-md-8 np-field">
                          <label>Card Name</label>
                          <input type="text" className="np-input" value={newCardName} onChange={(e) => setNewCardName(e.target.value)} placeholder="e.g., Custom Section" />
                        </div>
                      </div>
                      <div>
                        <button className="np-btn np-btn-sm np-btn-primary me-2" onClick={addCard} disabled={updateCardsMutation.isPending}>
                          Add Card
                        </button>
                        <button className="np-btn np-btn-sm np-btn-ghost" onClick={() => setAddCardMode(false)}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <NimbusTable thead={<tr>
                        <th>Card #</th>
                        <th>Card Name</th>
                        <th>Fields</th>
                        <th>Actions</th>
                      </tr>}>
                      {cardOptions.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="text-center np-sub py-4"><i className="ri-layout-grid-line me-1"></i>No cards defined</td>
                        </tr>
                      ) : (
                        cardOptions.map((c) => {
                          const fieldsInCard = (configs || []).filter((x) => Number(x.cardNumber) === c.value).length;
                          return (
                            <tr key={c.value}>
                              <td>
                                <span className="np-chip np-tone-primary np-tabular">{c.value}</span>
                              </td>
                              <td>
                                <strong>{cards[String(c.value)]}</strong>
                              </td>
                              <td className="np-tabular">{fieldsInCard} field(s)</td>
                              <td>
                                <div className="d-flex gap-1">
                                  <button className="np-icon-btn" onClick={() => openEditCardModal(c.value)} title="Edit">
                                    <i className="ri-edit-line"></i>
                                  </button>
                                  <button className="np-icon-btn np-icon-btn-danger" onClick={() => void deleteCard(c.value)} title="Delete">
                                    <i className="ri-delete-bin-line"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                </NimbusTable>
          </NimbusModalContent>
        </NimbusModal>
      )}

      {/* Edit Card */}
      {showEditCard && (
        <NimbusModal>
          <NimbusModalContent
            title="Edit Card"
            onClose={() => setShowEditCard(false)}
            footer={
              <>
                <button className="np-btn np-btn-ghost" onClick={() => setShowEditCard(false)}>
                  Cancel
                </button>
                <button className="np-btn np-btn-primary" onClick={saveEditedCard} disabled={updateCardsMutation.isPending}>
                  Save
                </button>
              </>
            }
          >
                <div className="np-field">
                  <label>Card #</label>
                  <input type="number" className="np-input np-tabular" value={editCardNumberValue} disabled />
                </div>
                <div className="np-field">
                  <label>Card Name</label>
                  <input type="text" className="np-input" value={editCardNameValue} onChange={(e) => setEditCardNameValue(e.target.value)} />
                </div>
          </NimbusModalContent>
        </NimbusModal>
      )}
    </div>
  );
};
